import { z } from "zod/v4";
import {
	MAX_GENDER_CHANGES,
	MAX_SKILL_CHANGES,
	skillLevelSchema,
	userGenderSchema,
} from "./shared";

export const localeSchema = z.enum(["de", "en"]);
export const themePreferenceSchema = z.enum(["system", "light", "dark"]);

// Glicko-2 rating, XP/level, and streak data (embedded in ProfileResponse).
export const profileProgressionSchema = z.object({
	computed_tier: skillLevelSchema,
	has_rating: z.boolean().default(false),
	is_provisional: z.boolean(),
	level: z.number().int(),
	level_progress: z.number(),
	level_title: z.string(),
	peak_rating: z.number(),
	play_streak_days: z.number().int(),
	rating: z.number(),
	rating_deviation: z.number(),
	total_xp: z.number().int(),
	weekly_streak: z.number().int(),
	win_streak: z.number().int(),
});

export const profileSchema = z.object({
	avatar_url: z.string().nullable().optional(),
	ban_reason: z.string().nullable().optional(),
	// Admin-only. Present on /admin/profiles responses, absent everywhere else,
	// so both are optional as well as nullable: `undefined` means "this endpoint
	// does not report bans", `null` means "not banned".
	banned_at: z.coerce.date().nullable().optional(),
	banner_url: z.string().nullable().optional(),
	bio: z.string().nullable().optional(),
	city: z.string().nullable().optional(),
	created_at: z.coerce.date(),
	display_name: z.string(),
	elo_rating: z.number().int(),
	email: z.string(),
	email_verified: z.boolean(),
	games_played: z.number().int(),
	games_won: z.number().int(),
	// Gender rides the same confirm-then-budget rule as skill: the pick locks
	// once confirmed and the budget is spent. `null` means never set.
	gender: userGenderSchema.nullable().optional(),
	gender_changes_remaining: z
		.number()
		.int()
		.min(0)
		.max(MAX_GENDER_CHANGES)
		.default(MAX_GENDER_CHANGES),
	gender_confirmed: z.boolean().default(false),
	id: z.string().uuid(),
	instagram_handle: z.string().nullable().optional(),
	is_looking_for_game: z.boolean(),
	locale: localeSchema,
	looking_until: z.coerce.date().nullable().optional(),
	onboarded: z.boolean(),
	skill_changes_remaining: z
		.number()
		.int()
		.min(0)
		.max(MAX_SKILL_CHANGES)
		.default(MAX_SKILL_CHANGES),
	skill_level: skillLevelSchema,
	skill_level_confirmed: z.boolean().default(true),
	theme_preference: themePreferenceSchema,
	username: z.string().nullable().optional(),
	win_rate: z.number(),
	...profileProgressionSchema.shape,
});

export const createProfileSchema = profileSchema.pick({
	display_name: true,
	email: true,
	skill_level: true,
});

export const updateProfileSchema = createProfileSchema.partial();
